import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Plus, Edit, Trash2, Copy, Video, Printer, Minus, Plus as PlusIcon, X, Film } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { printTickets } from '@/services/ticketPrinterService';
import { genericVideoService } from '@/services/genericVideoService';
import { adminRestaurantService } from '@/services/adminRestaurantService';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';

interface QRCodeCache {
  [slug: string]: string;
}

function VideoPreview({ src, thumbnailUrl }: { src?: string | null; thumbnailUrl?: string | null }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [canPlay, setCanPlay] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
    if (videoRef.current && canPlay) {
      videoRef.current.play().catch(() => {});
    }
  }, [canPlay]);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  if (!src) {
    return (
      <div className="w-28 h-44 bg-gray-100 rounded-lg flex items-center justify-center">
        <Video className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <div
      className="w-28 h-44 rounded-lg overflow-hidden relative bg-black group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        loop
        preload="auto"
        onCanPlay={() => setCanPlay(true)}
        className="w-full h-full object-cover"
      />
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${isHovering ? 'opacity-0' : 'opacity-100'}`}
        />
      )}
      <div className={`absolute top-1.5 left-1.5 bg-purple-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 transition-opacity duration-200 ${isHovering ? 'opacity-0' : 'opacity-100'}`}>
        <Film className="w-2.5 h-2.5" />
        VIDEO
      </div>
      <div className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-200 ${isHovering ? 'opacity-0' : 'opacity-100'}`}>
        <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center">
          <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[8px] border-l-gray-800 border-b-[5px] border-b-transparent ml-0.5" />
        </div>
      </div>
    </div>
  );
}

function PrintDialog({
  videoName,
  onPrint,
  onCancel,
  isPrinting,
}: {
  videoName: string;
  onPrint: (quantity: number) => void;
  onCancel: () => void;
  isPrinting: boolean;
}) {
  const [quantity, setQuantity] = useState(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-2xl p-6 w-80 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Imprimir QR</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-600">{videoName}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
            className="w-9 h-9 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className="text-lg font-medium w-8 text-center">{quantity}</span>
          <button
            onClick={() => setQuantity(Math.min(20, quantity + 1))}
            disabled={quantity >= 20}
            className="w-9 h-9 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </div>
        <Button
          onClick={() => onPrint(quantity)}
          disabled={isPrinting}
          className="w-full"
        >
          <Printer className="w-4 h-4 mr-2" />
          {isPrinting ? 'Imprimiendo...' : `Imprimir ${quantity} QR${quantity > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}

export function GenericVideosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter] = useState('this_week');
  const [qrCodes, setQrCodes] = useState<QRCodeCache>({});
  const [printingSlug, setPrintingSlug] = useState<string | null>(null);
  const [printDialogSlug, setPrintDialogSlug] = useState<string | null>(null);
  const isPrintingRef = useRef<string | null>(null);

  const { data: videos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['admin-generic-videos'],
    queryFn: () => genericVideoService.list(),
  });

  const { data: dashboard } = useQuery({
    queryKey: ['admin-generic-videos-dashboard', filter],
    queryFn: () => genericVideoService.dashboard(filter),
  });

  const { data: adminRestaurant } = useQuery({
    queryKey: ['admin-restaurant'],
    queryFn: () => adminRestaurantService.getRestaurant(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => genericVideoService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-generic-videos'] });
      toast.success('Video genérico eliminado');
    },
    onError: () => toast.error('Error al eliminar el video'),
  });

  useEffect(() => {
    const generateQRCodes = async () => {
      for (const video of videos) {
        try {
          const qrUrl = `${window.location.origin}/g/${video.slug}`;
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: 400,
            margin: 1,
            color: { dark: '#000000', light: '#FFFFFF' },
          });
          setQrCodes((prev) => {
            if (prev[video.slug]) return prev;
            return { ...prev, [video.slug]: qrDataUrl };
          });
        } catch {
          continue;
        }
      }
    };

    if (videos.length > 0) {
      generateQRCodes();
    }
  }, [videos]);

  const copyQrUrl = (slug: string) => {
    const url = `${window.location.origin}/g/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL del QR copiada');
  };

  const handlePrint = async (slug: string, quantity: number) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = slug;
    setPrintingSlug(slug);

    const qrDataUrl = qrCodes[slug];
    if (!qrDataUrl) {
      toast.error('El código QR aún no está listo. Espera un momento.');
      setPrintingSlug(null);
      isPrintingRef.current = null;
      return;
    }

    try {
      const result = await printTickets(
        { qrCodeUrl: qrDataUrl, logoUrl: adminRestaurant?.logo_url },
        {
          count: quantity,
          onProgress: (current, total) => {
            toast.info(`Imprimiendo ticket ${current}/${total}...`);
          },
        }
      );

      if (result.failed > 0) {
        toast.error(`Error al imprimir ${result.failed} ticket(s). ${result.success} impresos correctamente.`);
      } else {
        toast.success(`${quantity} QR${quantity > 1 ? 's' : ''} enviado${quantity > 1 ? 's' : ''} a la impresora`);
      }
    } catch {
      toast.error('Error al imprimir. Verifica la impresora.');
    } finally {
      setPrintingSlug(null);
      setPrintDialogSlug(null);
      isPrintingRef.current = null;
    }
  };

  if (videosLoading) return <PageLoading />;

  const printDialogVideo = printDialogSlug
    ? videos.find((v) => v.slug === printDialogSlug)
    : null;

  return (
    <AdminPageShell>
      <PageHeader title="Videos Genéricos" subtitle="Gestiona los videos genéricos para recuerdos sin comanda">
        <AdminButton onClick={() => navigate('/admin/generic-videos/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Video
        </AdminButton>
      </PageHeader>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard label="Recuerdos totales" value={dashboard.memories_total} />
          <StatCard label="Nuevos" value={dashboard.new_memories} />
          <StatCard label="Recurrentes" value={dashboard.recurring_memories} />
          <StatCard label="Visualizaciones" value={dashboard.video_views} />
          <StatCard label="Tasa visualización" value={`${dashboard.view_rate}%`} />
        </div>
      )}

      <div className="space-y-3">
        {videos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Video className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay videos genéricos</p>
              <p className="text-sm">Crea tu primer video genérico para empezar a generar recuerdos</p>
            </CardContent>
          </Card>
        ) : (
          videos.map((video) => (
            <Card key={video.id} className="hover:shadow-md transition-shadow overflow-hidden">
              <CardContent className="flex items-center gap-4 p-4">
                <VideoPreview src={video.video_file_url} thumbnailUrl={video.thumbnail_url} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{video.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {video.memories_count} recuerdos · {video.video_views_count} vistas
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">
                    /g/{video.slug}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPrintDialogSlug(video.slug)}
                    disabled={!qrCodes[video.slug] || printingSlug === video.slug}
                    title="Imprimir QR"
                  >
                    {printingSlug === video.slug ? (
                      <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin inline-block" />
                    ) : (
                      <Printer className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyQrUrl(video.slug)}
                    title="Copiar URL del QR"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/generic-videos/${video.id}/edit`)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm('¿Eliminar este video genérico?')) {
                        deleteMutation.mutate(video.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {printDialogVideo && (
        <PrintDialog
          videoName={printDialogVideo.name}
          isPrinting={printingSlug === printDialogVideo.slug}
          onPrint={(qty) => handlePrint(printDialogVideo.slug, qty)}
          onCancel={() => setPrintDialogSlug(null)}
        />
      )}
    </AdminPageShell>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
